import React from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
// import LocationOnIcon from "@mui/icons-material/LocationOn";
import Autocomplete, {
  AutocompleteRenderInputParams,
} from "@mui/material/Autocomplete";

import { api } from "api";
import { debounce } from "lodash";
import { PlaceType, IGraphhopperHit } from "types";
import { Stack } from "@mui/material";

export type AutoCompleteOption = {
  label: string;
  subtitle?: string;
};

type DefaultProps = {
  label?: string;
  disabled?: boolean;
  placeholder: string;
};

type LocationAutoCompleteProps<T> = DefaultProps & {
  value?: T | null;
  options: T[];
  onOpen?: () => void;
  onClose?: () => void;
  onValueChange?:
    | ((value: T | null) => void)
    | ((value: T | null, index: number) => void);
  onInputValueChange: (text: string) => void;
};

export const LocationAutoComplete = ({
  label,
  value,
  options,
  placeholder,
  onOpen,
  onClose,
  onValueChange,
  disabled = false,
  onInputValueChange,
}: LocationAutoCompleteProps<AutoCompleteOption>) => {
  const renderInput = (params: AutocompleteRenderInputParams) => {
    return (
      <TextField
        {...params}
        label={placeholder}
        // fullWidth
        // margin="dense"
        InputLabelProps={{
          shrink: true,
          // margin: "dense",
          focused: true,
          size: "small",
          sx: { fontFamily: ["Oswald", "sans-serif", "Roboto"].join(",") },
          // sx: { fontSize: "20px", fontWeight: 400 },
        }}
      />
    );
  };

  return (
    <Autocomplete
      value={value}
      autoComplete
      size="small"
      isOptionEqualToValue={(option, value) =>
        option.label === value.label && option.subtitle === value.subtitle
      }
      options={options}
      disabled={disabled}
      filterSelectedOptions
      includeInputInList
      filterOptions={(x) => x}
      renderInput={renderInput}
      onOpen={onOpen}
      onClose={onClose}
      style={{ flex: 1 }}
      id={`${label}-google-map-demo`}
      getOptionLabel={(option) => option.label}
      onChange={(_, newValue: AutoCompleteOption | null) => {
        // if (onValueChange) {
        const index = options.findIndex((i) => i.label === newValue?.label);
        if (onValueChange) {
          onValueChange(newValue, index || 0);
        }

        // }
      }}
      onInputChange={(_, newInputValue, reason) => {
        onInputValueChange(newInputValue);
      }}
      renderOption={(optionProps, option) => {
        return (
          <li
            {...optionProps}
            key={`${placeholder}-${option.label}-${optionProps.id}`}
          >
            <Stack flex={1}>
              <Typography variant="body2" style={{ fontSize: "20px" }}>
                {option.label}
              </Typography>
              <Typography variant="caption" color="#939393">
                {option.subtitle}
              </Typography>
            </Stack>
          </li>
        );
      }}
    />
  );
};

type GoogleLocationAutoCompleteProps = LocationAutoCompleteProps<PlaceType>;

export const GoogleLocationAutoComplete = ({
  label,
  value,
  options,
  disabled,
  placeholder,
  onValueChange,
  onInputValueChange,
}: GoogleLocationAutoCompleteProps) => {
  const convertToOption = (item: PlaceType): AutoCompleteOption => {
    const matches =
      item.structured_formatting.main_text_matched_substrings || [];

    const parts = parse(
      item.structured_formatting.main_text,
      matches.map((match: any) => [match.offset, match.offset + match.length])
    );

    return {
      label: parts.map((item) => item.text).join(),
      subtitle: item.structured_formatting.secondary_text,
    };
  };

  const selectedValue = React.useMemo(
    () => (value ? convertToOption(value) : undefined),
    [value]
  );

  const autoCompleteOptions = React.useMemo(() => {
    return options.map(convertToOption);
  }, [options]);

  return (
    <LocationAutoComplete
      label={label}
      disabled={disabled}
      value={selectedValue}
      placeholder={placeholder}
      options={autoCompleteOptions}
      onInputValueChange={onInputValueChange}
      onValueChange={(_, index) => {
        if (onValueChange) {
          onValueChange(options.find((_, i) => i === index) ?? null, index);
        }
      }}
    />
  );
};

const convertHitToOption = (hit: IGraphhopperHit) =>
  ({
    label: `${hit.name} ${hit.country}, ${hit.countrycode}`,
    subtitle: `${hit.osm_value}`,
  } as AutoCompleteOption);

export type GraphHopperAutoCompleteProps = {
  value?: IGraphhopperHit | null;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: IGraphhopperHit | null) => void;
};

export const GraphHopperAutoComplete = ({
  value,
  disabled,
  onChange,
  placeholder,
}: GraphHopperAutoCompleteProps) => {
  const [text, setText] = React.useState("");
  const [isActive, setIsActive] = React.useState(false);
  const [data, setData] = React.useState<IGraphhopperHit[]>([]);

  const fetchh = React.useMemo(
    () =>
      debounce(
        async (q: string, callback: (hits: IGraphhopperHit[]) => void) => {
          const response = await api.graphhopper.search(q);
          callback(response.data.hits);
        },
        1000
      ),
    []
  );

  React.useEffect(() => {
    let active = true;

    if (!text || text === "") {
      setData(value ? [value] : []);
      // onChange(null);
      return undefined;
    }

    if (isActive) {
      fetchh(text, (result) => {
        if (active) {
          let newData: IGraphhopperHit[] = [];

          if (value) {
            newData = [value];
          }

          if (result) {
            newData = [...newData, ...result];
          }

          setData(newData);
        }
      });
    }

    return () => {
      active = false;
    };
  }, [value, fetchh, text, isActive]);

  const options = React.useMemo(() => {
    return data.map(convertHitToOption);
  }, [data]);

  return (
    <LocationAutoComplete
      options={options}
      disabled={disabled}
      placeholder={placeholder ?? ""}
      onClose={() => setIsActive(false)}
      value={value ? convertHitToOption(value) : null}
      onInputValueChange={(text) => {
        if (!text) {
          setIsActive(false);
          setText(text);
          return;
        }
        setIsActive(true);
        setText(text);
      }}
      onValueChange={(_, index) => {
        const option = data.find((_, i) => i === index);
        if (option) {
          setData((prev) => (option ? [option, ...prev] : prev));
          onChange(option);
        }
      }}
    />
  );
};
